import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { toast } from 'sonner'
import { client } from '@/client/client.gen'
import { refresh, logout as sdkLogout } from '@/client/sdk.gen'
import type { UserRole } from '@/client/types.gen'

const TOKEN_KEY = 'access_token'
const EMAIL_KEY = 'current_email'
const AUTH_EXPIRED = 'auth:expired'

// ─── JWT helpers ─────────────────────────────────────────────────────────────

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return {}
  }
}

function roleFromToken(token: string | null): UserRole | null {
  if (!token) return null
  const { role } = decodeJwtPayload(token)
  if (role === 'superadmin' || role === 'admin' || role === 'manager') return role
  return null
}

// ─── Client setup ────────────────────────────────────────────────────────────

client.setConfig({ baseUrl: '/api' })

client.interceptors.request.use((request) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) request.headers.set('Authorization', `Bearer ${token}`)
  return request
})

// ─── Token refresh ────────────────────────────────────────────────────────────

let refreshing: Promise<string | null> | null = null

async function tryRefreshToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = refresh({ throwOnError: true })
      .then(({ data }) => {
        const token = data?.access_token ?? null
        if (token) localStorage.setItem(TOKEN_KEY, token)
        return token
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY)
        return null
      })
      .finally(() => {
        refreshing = null
      })
  }
  return refreshing
}

client.interceptors.response.use(async (response, request, opts) => {
  if (response.status !== 401) return response

  const url = (opts as { url?: string }).url ?? ''
  if (url.startsWith('/auth/')) return response

  const newToken = await tryRefreshToken()

  if (!newToken) {
    window.dispatchEvent(new Event(AUTH_EXPIRED))
    return response
  }

  const headers = new Headers(request.headers)
  headers.set('Authorization', `Bearer ${newToken}`)

  return fetch(
    new Request(request.url, {
      method: request.method,
      headers,
      body: (opts as { serializedBody?: string }).serializedBody,
      credentials: request.credentials,
      mode: request.mode,
      cache: request.cache,
      redirect: request.redirect,
    }),
  )
})

// ─── Auth context ─────────────────────────────────────────────────────────────

interface AuthContextValue {
  isAuthenticated: boolean
  currentEmail: string | null
  role: UserRole | null
  setToken: (token: string, email: string) => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem(TOKEN_KEY),
  )
  const [currentEmail, setCurrentEmail] = useState<string | null>(
    () => localStorage.getItem(EMAIL_KEY),
  )
  const [role, setRole] = useState<UserRole | null>(
    () => roleFromToken(localStorage.getItem(TOKEN_KEY)),
  )

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Schedules a proactive token refresh ~1 minute before the JWT expires.
  // Re-arms itself after each successful refresh so the session stays alive
  // for users who are active but not making API calls.
  const scheduleTokenRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    const { exp } = decodeJwtPayload(token) as { exp?: number }
    if (!exp) return
    const delay = Math.max(0, exp * 1000 - Date.now() - 60_000)
    // setTimeout delay is stored as a signed 32-bit int; values above ~24.8 days
    // wrap to negative and fire immediately, causing an instant failed refresh.
    // Skip scheduling for tokens that expire far enough in the future.
    if (delay > 2_147_483_647) return
    refreshTimerRef.current = setTimeout(async () => {
      const newToken = await tryRefreshToken()
      if (!newToken) {
        window.dispatchEvent(new Event(AUTH_EXPIRED))
      } else {
        scheduleTokenRefresh(newToken)
      }
    }, delay)
  }, [])

  const setToken = useCallback((token: string, email: string) => {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(EMAIL_KEY, email)
    setIsAuthenticated(true)
    setCurrentEmail(email)
    setRole(roleFromToken(token))
    scheduleTokenRefresh(token)
  }, [scheduleTokenRefresh])

  const logout = useCallback(async () => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    try {
      await sdkLogout()
    } finally {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(EMAIL_KEY)
      setIsAuthenticated(false)
      setCurrentEmail(null)
      setRole(null)
    }
  }, [])

  // On mount: arm the refresh timer for any existing session, and re-arm
  // whenever the user returns to a tab that was idle long enough to let the
  // in-memory timer lapse.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (token) scheduleTokenRefresh(token)

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const t = localStorage.getItem(TOKEN_KEY)
        if (t) scheduleTokenRefresh(t)
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      if (refreshTimerRef.current !== null) clearTimeout(refreshTimerRef.current)
    }
  }, [scheduleTokenRefresh])

  useEffect(() => {
    const handle = () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(EMAIL_KEY)
      setIsAuthenticated(false)
      setCurrentEmail(null)
      setRole(null)
      toast.error('Session expired. Please sign in again.')
      window.location.href = '/login'
    }
    window.addEventListener(AUTH_EXPIRED, handle)
    return () => window.removeEventListener(AUTH_EXPIRED, handle)
  }, [])

  return (
    <AuthContext.Provider value={{ isAuthenticated, currentEmail, role, setToken, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
